import React, { useState, cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  makeStyles,
  withStyles,
  Divider as DividerMUI,
  Popover,
  Typography as TypographyMUI,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';

import IconButton from 'components/IconButton';
import { generateUUID } from 'utilities';

const useStyles = makeStyles((theme) => {
  return {
    modalContainer: {
      display: 'flex',
    },
    buttonContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      flexFlow: 'row wrap',
      justifyContent: 'space-between',
      padding: theme.spacing(1),
    },
    titleContainer: {
      display: 'flex',
      order: '1',
      flex: '1',
      alignSelf: 'center',
      paddingLeft: theme.spacing(1),
    },
    title: {
      fontSize: '1.175rem',
    },
    modalWithBorder: {
      borderLeft: (props) => props.borderLeftStyle,
    },
    modal: {
      maxWidth: '315px',
      minWidth: '300px',
    },
    noAnimation: {
      transitionDuration: '0s !important',
      '& *': {
        transitionDuration: '0s !important',
      },
    },
    screenReaderOnly: {
      clip: 'rect(1px, 1px, 1px, 1px)',
      position: 'absolute !important',
      height: '1px',
      width: '1px',
      overflow: 'hidden',
    },
    closeButton: {
      alignSelf: 'flex-end',
      order: 2,
    },
  };
});

const StyledPopover = withStyles((theme) => {
  return {
    paper: {
      maxWidth: '100%',
    },
  };
})(Popover);

const Modal = ({
  anchorPosition,
  animate = true,
  borderLeftColor = null,
  children,
  isOpen = true,
  onClose = () => {},
  borderLeftStyle,
  title, // A title is required even if hideTitle is true, as it will still be used for screen readers.
  hideTitle = false, // Whether the title should be rendered in an element that is visible to the user.
  ...props
}) => {
  const classes = useStyles({
    borderLeftStyle,
  });

  // Use the <Footer> height (scrub and/or tts bar)
  // maintains min of this space between side of modal and the edge of viewport
  let marginThreshold = document.getElementById('scrub-bar')?.clientHeight;

  const [
    // updatePosition,
    setUpdatePosition,
  ] = useState(false);
  const [modalUUID] = useState(generateUUID);

  const CloseButton = () => {
    return (
      <IconButton
        aria-label="Close"
        className={classes.closeButton}
        onClick={onClose}
      >
        <Close />
      </IconButton>
    );
  };

  const TitleComponent = () => {
    return (
      <>
        <div className={classes.buttonContainer}>
          <div className={classes.titleContainer}>
            <TypographyMUI
              variant="h2"
              className={classes.title}
              id={`modal-title-${modalUUID}`}
            >
              {title}
            </TypographyMUI>
          </div>
          <CloseButton />
        </div>
        <DividerMUI aria-hidden="true" />
      </>
    );
  };

  return (
    <StyledPopover
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      aria-labelledby={`modal-title-${modalUUID}`}
      className={clsx(
        classes.modalContainer,
        animate ? null : classes.noAnimation,
      )}
      marginThreshold={marginThreshold}
      onClose={onClose}
      open={isOpen}
      role="dialog"
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disablePortal
      {...props}
    >
      <div className={clsx(classes.modal, classes.modalWithBorder)}>
        {hideTitle ? (
          <div
            className={classes.screenReaderOnly}
            id={`modal-title-${modalUUID}`}
          >
            {title}
          </div>
        ) : (
          <TitleComponent />
        )}
        {isValidElement(children)
          ? cloneElement(children, { setUpdatePosition })
          : children}
      </div>
    </StyledPopover>
  );
};

Modal.propTypes = {
  anchorPosition: PropTypes.object,
  animate: PropTypes.bool,
  borderLeftColor: PropTypes.string,
  children: PropTypes.any.isRequired,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  borderLeftStyle: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default Modal;
